import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/ui/Navbar";
import PageSizeSelect from "../components/ui/PageSizeSelect";
import FormFirmarServicios from "../components/forms/FormFirmarServicios";
import { verReporteIndividual } from "../utils/reports";
import {
  asignarSticker,
  stickersPorCronogramas,
  urlCertificadoPublico,
} from "../utils/calibStickers";
import AsignarStickerModal from "../components/calibracion/AsignarStickerModal";
import FirmarRevisionModal from "../components/calibracion/FirmarRevisionModal";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Headset,
  LoaderPinwheel,
  Filter,
  Search,
  X,
  CalendarDays,
  TableProperties,
  Eye,
  Wrench,
  FileSignature,
  Tag,
  Lock,
  ClipboardCheck,
} from "lucide-react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const esCalibracionFinal = (s) =>
  s.tipo === "Calibración" && ["Completado", "Cerrado"].includes(s.estado);

const revisionPendiente = (s) =>
  s.esRevision && s.segundoFirmante?.estado === "pendiente";

const TiposMantenimiento = ["Preventivo", "Correctivo", "Calibración"];
const Estados = ["Pendiente", "Programado", "Borrador", "Completado", "Cerrado", "Cancelado"];
const PIE_COLORS = ["#3b82f6", "#10b981", "#f97316", "#ef4444"];
const FILTERS_STORAGE_KEY = "mis-servicios-filters";

const estadoCalendarColors = {
  Programado: "#3b82f6",
  Pendiente:  "#f59e0b",
  Completado: "#22c55e",
  Cerrado:    "#16a34a",
  Cancelado:  "#ef4444",
};

const ESTADO_BADGE = {
  Pendiente:  "bg-amber-50 text-amber-700 border border-amber-200",
  Programado: "bg-blue-50 text-blue-700 border border-blue-200",
  Borrador:   "bg-violet-50 text-violet-700 border border-violet-200",
  Completado: "bg-green-50 text-green-700 border border-green-200",
  Cerrado:    "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Cancelado:  "bg-red-50 text-red-700 border border-red-200",
};

const TIPO_BADGE = {
  Preventivo:   "bg-blue-50 text-blue-700 border border-blue-200",
  Correctivo:   "bg-orange-50 text-orange-700 border border-orange-200",
  "Calibración": "bg-violet-50 text-violet-700 border border-violet-200",
};

const getPersistedFilters = () => {
  try {
    const rawFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    return rawFilters ? JSON.parse(rawFilters) : {};
  } catch {
    return {};
  }
};

const ServiciosPage = () => {
  const [initialFilters] = useState(() => getPersistedFilters());
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState(initialFilters.search || "");
  const [filterTipo, setFilterTipo] = useState(initialFilters.tipo || "");
  const [filterEstado, setFilterEstado] = useState(initialFilters.estado || "");
  const [filterArea, setFilterArea] = useState(initialFilters.area || "");
  const [filterCliente, setFilterCliente] = useState(initialFilters.cliente || "");
  const [filterSede, setFilterSede] = useState(initialFilters.sede || "");
  const [areas, setAreas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRevision, setShowRevision] = useState(false);
  const [activeTab, setActiveTab] = useState("tabla");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  // Stickers de calibración asignados, por id de cronograma
  const [stickers, setStickers] = useState({});
  const [servicioSticker, setServicioSticker] = useState(null);
  const [asignando, setAsignando] = useState(false);

  const fetchServicios = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { sort: "-createdAt", incluirRevision: "1" };
      const currentSearch = opts.search ?? search;
      const currentTipo = opts.tipo ?? filterTipo;
      const currentEstado = opts.estado ?? filterEstado;
      const currentArea = opts.area ?? filterArea;

      if (currentSearch) params.search = currentSearch;
      if (currentTipo) params.tipo = currentTipo;
      if (currentEstado) params.estado = currentEstado;
      if (currentArea) params.area = currentArea;

      const res = await axios.get(`${API}/cronograma/my_services`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setServicios(res.data);
      setPage(1);
    } catch {
      toast.error("Error al cargar servicios");
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await axios.get(`${API}/areas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAreas(res.data);
    } catch {
      toast.error("Error al cargar áreas");
    }
  };

  const fetchClientes = async () => {
    try {
      const res = await axios.get(`${API}/clientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientes(res.data || []);
    } catch {
      toast.error("Error al cargar clientes");
    }
  };

  const fetchSedesByCliente = async (clienteId) => {
    if (!clienteId) { setSedes([]); return; }
    try {
      const res = await axios.get(`${API}/sedes/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(res.data || []);
    } catch {
      toast.error("Error al cargar sedes");
      setSedes([]);
    }
  };

  useEffect(() => {
    fetchServicios({
      search: initialFilters.search || "",
      area:   initialFilters.area   || "",
      tipo:   initialFilters.tipo   || "",
      estado: initialFilters.estado || "",
    });
    fetchAreas();
    fetchClientes();
    if (initialFilters.cliente) fetchSedesByCliente(initialFilters.cliente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({ search, cliente: filterCliente, sede: filterSede, area: filterArea, tipo: filterTipo, estado: filterEstado }),
    );
  }, [search, filterCliente, filterSede, filterArea, filterTipo, filterEstado]);

  const handleSearch = () => fetchServicios({ search });
  const handleFilterTipo   = (v) => { setFilterTipo(v);   fetchServicios({ tipo: v }); };
  const handleFilterEstado = (v) => { setFilterEstado(v); fetchServicios({ estado: v }); };
  const handleFilterArea   = (v) => { setFilterArea(v);   fetchServicios({ area: v }); };

  const handleFilterCliente = async (v) => {
    setFilterCliente(v);
    setFilterSede("");
    await fetchSedesByCliente(v);
  };

  const handleFilterSede = (v) => setFilterSede(v);

  const handleClearFilters = async () => {
    setSearch("");
    setFilterCliente("");
    setFilterSede("");
    setFilterArea("");
    setFilterTipo("");
    setFilterEstado("");
    setSedes([]);
    await fetchServicios({ search: "", area: "", tipo: "", estado: "" });
  };

  const filteredServicios = useMemo(() => {
    return servicios.filter((s) => {
      const clienteId = s.equipo?.cliente?._id || s.equipo?.cliente;
      const sedeId    = s.equipo?.sede?._id    || s.equipo?.sede;
      return (filterCliente ? clienteId === filterCliente : true)
          && (filterSede    ? sedeId    === filterSede    : true);
    });
  }, [servicios, filterCliente, filterSede]);

  const hasActiveFilters = Boolean(
    search || filterCliente || filterSede || filterArea || filterTipo || filterEstado,
  );

  // Las revisiones de segunda firma no son servicios ejecutados por el usuario:
  // no cuentan en su cumplimiento ni en su calendario
  const serviciosPropios = useMemo(
    () => filteredServicios.filter((s) => !s.esRevision),
    [filteredServicios],
  );
  const revisionesPendientes = useMemo(
    () => servicios.filter(revisionPendiente).length,
    [servicios],
  );

  const cumplimientoData = useMemo(() => {
    if (!serviciosPropios.length) return { data: [], porcentaje: 0 };
    const programados = serviciosPropios.filter((s) => s.estado === "Programado").length;
    const completados = serviciosPropios.filter((s) => s.estado === "Completado" || s.estado === "Cerrado").length;
    const pendientes  = serviciosPropios.filter((s) => s.estado === "Pendiente").length;
    const total = programados + completados + pendientes;
    return {
      data: [
        { name: "Completados", value: completados },
        { name: "Programados", value: programados },
        { name: "Pendientes",  value: pendientes  },
      ],
      porcentaje: total > 0 ? ((completados / total) * 100).toFixed(1) : 0,
      completados,
      programados,
      pendientes,
    };
  }, [serviciosPropios]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredServicios.slice(start, start + pageSize);
  }, [filteredServicios, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredServicios.length / pageSize));

  const calendarEvents = useMemo(
    () =>
      serviciosPropios
        .filter((s) => s.fechaProgramada)
        .map((s) => ({
          id: s._id,
          title: `${s.tipo || "Servicio"} · ${s.equipo?.serial || "Sin serial"}`,
          start: s.fechaProgramada,
          backgroundColor: estadoCalendarColors[s.estado] || "#9ca3af",
          borderColor:     estadoCalendarColors[s.estado] || "#9ca3af",
          extendedProps: { cliente: s.equipo?.cliente?.name || "—" },
        })),
    [serviciosPropios],
  );

  useEffect(() => {
    setPage(1);
  }, [search, filterCliente, filterSede, filterArea, filterTipo, filterEstado]);

  // Calibraciones finalizadas: consultar qué código de certificado tienen
  useEffect(() => {
    const ids = (servicios || [])
      .filter((s) => esCalibracionFinal(s))
      .map((s) => s._id);
    if (!ids.length) return;
    stickersPorCronogramas(ids)
      .then(setStickers)
      .catch(() => {});
  }, [servicios]);

  const handleAsignarSticker = async (stickerId) => {
    if (!servicioSticker) return;
    setAsignando(true);
    try {
      const res = await asignarSticker(servicioSticker._id, stickerId);
      setStickers((prev) => ({ ...prev, [servicioSticker._id]: res.sticker }));
      toast.success(`Código ${res.sticker.numero} asignado`);
      setServicioSticker(null);
    } catch (err) {
      const existente = err.response?.data?.sticker;
      if (existente) {
        setStickers((prev) => ({ ...prev, [servicioSticker._id]: existente }));
        setServicioSticker(null);
      }
      toast.error(err.response?.data?.message || "No se pudo asignar el código");
    } finally {
      setAsignando(false);
    }
  };

  const handleVerReporte = async (id) => {
    setIsLoading(id);
    await verReporteIndividual(id);
    setIsLoading(null);
  };

  if (!servicios)
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-600">
        <LoaderPinwheel className="w-10 h-10 mb-3 animate-spin text-blue-600" />
        <p>Cargando mis servicios...</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-5">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Headset className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mis Servicios</h1>
              <p className="text-sm text-gray-500">
                Mantenimientos preventivos, correctivos y calibraciones
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {revisionesPendientes > 0 && (
              <button
                onClick={() => setShowRevision(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <ClipboardCheck className="h-4 w-4" />
                Revisar y firmar
                <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-xs font-semibold">
                  {revisionesPendientes}
                </span>
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <FileSignature className="h-4 w-4" />
              Autorizar servicio
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        {/* Cumplimiento */}
        {serviciosPropios.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
              Nivel de Cumplimiento
            </h2>
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              {/* Pie */}
              <div className="w-full lg:w-60 h-52 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cumplimientoData.data}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {cumplimientoData.data.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 w-full">
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{cumplimientoData.porcentaje}%</p>
                  <p className="text-xs text-blue-500 mt-1 font-medium">Cumplimiento</p>
                </div>
                <div className="rounded-xl bg-green-50 border border-green-100 p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{cumplimientoData.completados}</p>
                  <p className="text-xs text-green-500 mt-1 font-medium">Completados</p>
                </div>
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 text-center">
                  <p className="text-3xl font-bold text-indigo-600">{cumplimientoData.programados}</p>
                  <p className="text-xs text-indigo-500 mt-1 font-medium">Programados</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{cumplimientoData.pendientes}</p>
                  <p className="text-xs text-amber-500 mt-1 font-medium">Pendientes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Servicios */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">

          {/* Filtros */}
          <div className="p-4 sm:p-5 border-b border-gray-100">
            <div className="flex flex-wrap items-center gap-3">

              {/* Búsqueda */}
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Buscar por código o cliente..."
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(""); fetchServicios({ search: "" }); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <select
                value={filterCliente}
                onChange={(e) => handleFilterCliente(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              >
                <option value="">Todos los clientes</option>
                {clientes.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>

              <select
                value={filterSede}
                onChange={(e) => handleFilterSede(e.target.value)}
                disabled={!filterCliente}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">Todas las sedes</option>
                {sedes.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>

              <select
                value={filterArea}
                onChange={(e) => handleFilterArea(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              >
                <option value="">Todas las áreas</option>
                {areas.map((a) => (
                  <option key={a._id} value={a._id}>{a.name}</option>
                ))}
              </select>

              <select
                value={filterTipo}
                onChange={(e) => handleFilterTipo(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              >
                <option value="">Todos los tipos</option>
                {TiposMantenimiento.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <select
                value={filterEstado}
                onChange={(e) => handleFilterEstado(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              >
                <option value="">Todos los estados</option>
                {Estados.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Limpiar
                </button>
              )}
            </div>

            {hasActiveFilters && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-full text-xs font-medium">
                  <Filter className="h-3 w-3" />
                  Filtros activos · {filteredServicios.length} resultado{filteredServicios.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>

          {/* Barra vista + contador */}
          <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              {filteredServicios.length} servicio{filteredServicios.length !== 1 ? "s" : ""}
            </p>
            <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
              <button
                onClick={() => setActiveTab("tabla")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === "tabla"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <TableProperties className="h-4 w-4" />
                Tabla
              </button>
              <button
                onClick={() => setActiveTab("calendario")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === "calendario"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <CalendarDays className="h-4 w-4" />
                Calendario
              </button>
            </div>
          </div>

          {/* Tabla */}
          {activeTab === "tabla" && (
            <div>
              <div className="flex items-center justify-end mb-2">
                <PageSizeSelect
                  value={pageSize}
                  onChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-100">
                      <th className="px-4 py-3 text-left">Cliente</th>
                      <th className="px-4 py-3 text-left">Equipo</th>
                      <th className="px-4 py-3 text-left">Área</th>
                      <th className="px-4 py-3 text-left">Tipo</th>
                      <th className="px-4 py-3 text-left">Fecha</th>
                      <th className="px-4 py-3 text-left">Estado</th>
                      <th className="px-4 py-3 text-left">Responsable</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-gray-400">
                          <LoaderPinwheel className="h-6 w-6 animate-spin text-blue-400 mx-auto mb-2" />
                          Cargando servicios...
                        </td>
                      </tr>
                    ) : paged.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-14 text-center">
                          <Headset className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                          <p className="text-gray-500 font-medium">No se encontraron servicios</p>
                          <p className="text-gray-400 text-sm mt-0.5">Ajusta los filtros o verifica tu asignación</p>
                        </td>
                      </tr>
                    ) : (
                      paged.map((s) => (
                        <tr key={s._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{s.equipo?.cliente?.name || "—"}</p>
                            {s.equipo?.cliente?.nit && (
                              <p className="text-xs text-gray-400">NIT {s.equipo.cliente.nit}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/equipos/${s.equipo?._id}`)}
                              className="text-blue-600 hover:underline text-left font-medium"
                            >
                              {s.equipo?.marca?.name || "—"} {s.equipo?.modelo?.name || ""}
                            </button>
                            {s.equipo?.serial && (
                              <p className="text-xs text-gray-400 font-mono">{s.equipo.serial}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {s.equipo?.area?.grupo?.name && (
                              <p className="text-xs text-gray-400">{s.equipo.area.grupo.name}</p>
                            )}
                            {s.equipo?.area?.name || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${TIPO_BADGE[s.tipo] || "bg-gray-100 text-gray-600"}`}>
                              {s.tipo || "—"}
                            </span>
                            {s.esRevision && (
                              <span
                                className={`mt-1 block w-fit px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  revisionPendiente(s)
                                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}
                              >
                                {revisionPendiente(s) ? "Calibración para revisión" : "Revisada"}
                              </span>
                            )}
                            {s.estado === "Borrador" && s.segundoFirmante?.ultimaDevolucion?.motivo && (
                              <span
                                title={s.segundoFirmante.ultimaDevolucion.motivo}
                                className="mt-1 block w-fit max-w-[14rem] truncate px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200"
                              >
                                Devuelta: {s.segundoFirmante.ultimaDevolucion.motivo}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {new Date(s.fechaProgramada).toLocaleDateString("es-CO")}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${ESTADO_BADGE[s.estado] || "bg-gray-100 text-gray-600"}`}>
                              {s.estado}
                            </span>
                            {s.vencido && (
                              <span className="ml-1 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                                Vencido
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {s.personaAsignada?.name || (
                              <span className="text-gray-300 italic text-xs">No asignado</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                            {revisionPendiente(s) && (
                              <button
                                onClick={() => setShowRevision(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                              >
                                <ClipboardCheck className="h-3.5 w-3.5" />
                                Revisar y firmar
                              </button>
                            )}
                            {esCalibracionFinal(s) &&
                              (stickers[s._id] ? (
                                <button
                                  onClick={() =>
                                    window.open(urlCertificadoPublico(stickers[s._id].token), "_blank")
                                  }
                                  title="Código de certificado asignado (no editable)"
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-semibold bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  <Lock className="h-3 w-3 text-gray-400" />
                                  {stickers[s._id].numero}
                                </button>
                              ) : s.esRevision ? null : (
                                <button
                                  onClick={() => setServicioSticker(s)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                                >
                                  <Tag className="h-3.5 w-3.5" />
                                  Asignar código
                                </button>
                              ))}
                            {["Pendiente", "Borrador"].includes(s.estado) ? (
                              <button
                                onClick={() => navigate(`/manage/${s._id}`, { state: { servicio: s } })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                              >
                                <Wrench className="h-3.5 w-3.5" />
                                {s.estado === "Borrador" ? "Retomar" : "Gestionar"}
                              </button>
                            ) : s.tipo === "Correctivo" && s.estado === "Completado" ? (
                              <button
                                onClick={() => navigate(`/manage/${s._id}`, { state: { servicio: s } })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                Ver
                              </button>
                            ) : s.tipo === "Calibración" && s.estado === "Completado" ? (
                              <button
                                onClick={() => handleVerReporte(s._id)}
                                disabled={isLoading === s._id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-60"
                              >
                                {isLoading === s._id
                                  ? <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                                  : <Eye className="h-3.5 w-3.5" />
                                }
                                {isLoading === s._id ? "Cargando..." : "Ver certificado"}
                              </button>
                            ) : s.tipo === "Preventivo" && s.estado === "Completado" ? (
                              <button
                                onClick={() => navigate(`/manage/${s._id}`, { state: { servicio: s } })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                Ver
                              </button>
                            ) : s.estado === "Cerrado" ? (
                              <button
                                onClick={() => handleVerReporte(s._id)}
                                disabled={isLoading === s._id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-60"
                              >
                                {isLoading === s._id
                                  ? <LoaderPinwheel className="h-3.5 w-3.5 animate-spin" />
                                  : <Eye className="h-3.5 w-3.5" />
                                }
                                {isLoading === s._id ? "Cargando..." : "Ver acta"}
                              </button>
                            ) : (
                              <span className="text-gray-300 italic text-xs">No editable</span>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {filteredServicios.length === 0
                    ? "0"
                    : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filteredServicios.length)}`
                  } de {filteredServicios.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Anterior
                  </button>
                  <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Calendario */}
          {activeTab === "calendario" && (
            <div className="p-4">
              <FullCalendar
                plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                locale={esLocale}
                events={calendarEvents}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: "prev,next",
                  center: "title",
                  right: "dayGridMonth,listWeek",
                }}
                dayMaxEventRows={3}
                eventClick={(info) =>
                  navigate(`/manage/${info.event.id}`, {
                    state: {
                      servicio: filteredServicios.find((s) => s._id === info.event.id) || null,
                    },
                  })
                }
                eventContent={(arg) => (
                  <div className="text-xs leading-tight overflow-hidden px-0.5">
                    <div className="font-semibold truncate">{arg.event.title}</div>
                    <div className="truncate opacity-80">{arg.event.extendedProps.cliente}</div>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal autorización */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl relative">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-gray-800">Autorizar Servicio</h3>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <FormFirmarServicios
                onSigned={async () => {
                  await fetchServicios();
                  setShowForm(false);
                }}
                onClose={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showRevision && (
        <FirmarRevisionModal
          onClose={() => setShowRevision(false)}
          onCambio={fetchServicios}
        />
      )}

      {/* Asignar código de certificado a una calibración finalizada */}
      <AsignarStickerModal
        open={!!servicioSticker}
        onClose={() => setServicioSticker(null)}
        procesando={asignando}
        titulo="Asignar código de certificado"
        textoBoton="Asignar definitivamente"
        aviso={
          servicioSticker
            ? `Servicio: ${servicioSticker.equipo?.marca?.name || ""} ${servicioSticker.equipo?.modelo?.name || ""} · Serial ${servicioSticker.equipo?.serial || "—"} · ${new Date(servicioSticker.fechaProgramada).toLocaleDateString("es-CO")}`
            : undefined
        }
        onConfirm={handleAsignarSticker}
      />
    </div>
  );
};

export default ServiciosPage;
